/**
 * Date formatting utilities.
 *
 * Why this exists:
 *   The backend stores all timestamps as UTC (LocalDateTime in Java, serialised
 *   to JSON without a timezone suffix, e.g. "2024-01-15T10:30:00").
 *
 *   JavaScript's new Date("2024-01-15T10:30:00") treats a string WITHOUT a "Z"
 *   suffix as LOCAL time (IST in India). That would show 10:30 AM even though the
 *   actual event happened at 10:30 UTC = 4:00 PM IST — 5.5 hours wrong.
 *
 *   These helpers append "Z" before parsing so the browser knows it's UTC, then
 *   always format in IST (Asia/Kolkata) so the displayed time is always correct
 *   Indian time regardless of which timezone the browser is in.
 *
 * Usage:
 *   import { formatDate, formatDateTime, formatTime, formatDateLong } from 'utils/dateUtils';
 *
 *   formatDate('2024-01-15T10:30:00')        → "15 Jan 2024"
 *   formatDateTime('2024-01-15T10:30:00')    → "15 Jan 2024, 04:00 pm"
 *   formatTime('2024-01-15T10:30:00')        → "04:00 pm"
 *   formatDateLong('2024-01-15T10:30:00')    → "15 January 2024"
 */

const IST = 'Asia/Kolkata';

/**
 * Parse a datetime string from the backend API as UTC.
 * Appends "Z" if the string has no timezone offset so JavaScript treats it as UTC.
 * Returns null if the input is falsy or results in an invalid date.
 */
function parseApiDate(str) {
  if (!str) return null;
  // Already has timezone info (ends with Z or contains + offset like +05:30)
  const normalized = (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str))
    ? str
    : str + 'Z';
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Short date: "15 Jan 2024"
 */
export function formatDate(str) {
  const d = parseApiDate(str);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: IST,
  });
}

/**
 * Long date: "15 January 2024"
 */
export function formatDateLong(str) {
  const d = parseApiDate(str);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: IST,
  });
}

/**
 * Date + time: "15 Jan 2024, 04:00 pm"
 */
export function formatDateTime(str) {
  const d = parseApiDate(str);
  if (!d) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: IST,
  });
}

/**
 * Time only: "04:00 pm"
 */
export function formatTime(str) {
  const d = parseApiDate(str);
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', timeZone: IST,
  });
}
